import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Upload, Download, Wand2, Scissors, Image as ImageIcon, Palette, Loader2, AlertCircle, Check, Copy, ExternalLink, Brush, Sparkles, Sliders, Square, Smartphone, RectangleVertical, Monitor, RectangleHorizontal, Gift, Gem } from 'lucide-react';
import { generateImage, removeBackground, fluxEdit, qwenEdit, getUserCredits } from '../../services/apiService';
import AdvancedEditModal from './AdvancedEditModal';

// Constants — Lucide icon components instead of emoji strings
const ASPECT_RATIOS = [
  { label: 'Square',    value: 'Square (1:1)',    Icon: Square },
  { label: 'Portrait',  value: 'Portrait (3:4)',  Icon: RectangleVertical },
  { label: 'Mobile',    value: 'Mobile (9:16)',   Icon: Smartphone },
  { label: 'Landscape', value: 'Landscape (4:3)', Icon: Monitor },
  { label: 'Wide',      value: 'Wide (16:9)',     Icon: RectangleHorizontal }
];

// Helper to hash strings for cache keys
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < Math.min(str.length, 1000); i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
  }
  return hash.toString(36);
};

const ImageGeneratorModal = ({ isOpen, onClose }) => {
  // State management
  const [activeTab, setActiveTab] = useState('generate');
  const [prompt, setPrompt] = useState('');
  const [editPrompt, setEditPrompt] = useState('');
  const [qwenEditPrompt, setQwenEditPrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState('black-forest-labs/FLUX-2-klein-4b:free');
  const [aspectRatio, setAspectRatio] = useState('Square (1:1)');
  const [quality, setQuality] = useState('free');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [credits, setCredits] = useState(null);
  const [isAdvancedEditOpen, setIsAdvancedEditOpen] = useState(false);
  const [fluxQuality, setFluxQuality] = useState('dev');
  const [imageStorageUrls, setImageStorageUrls] = useState(new Map());
  const [lastGenerateTime, setLastGenerateTime] = useState(0);

  // ✅ FIXED: Memoized image models with dynamic allowances
  const imageModels = useMemo(() => {
    // SD3/Qwen monthly-allowance models removed: the QXAI image route does not
    // enforce allowances, so the counter was cosmetic. Free tier is now the
    // genuinely $0 ImageRouter models (verified slugs).
    const freeAllowanceModels = [];

    const alwaysFreeModels = [
      { id: 'black-forest-labs/FLUX-2-klein-4b:free', name: 'FLUX 2 Klein', description: 'Default · Fast & free', credits: 0, category: 'always-free' },
      { id: 'google/nano-banana-2:free', name: 'Nano Banana 2', description: 'Google · Free', credits: 0, category: 'always-free' },
      { id: 'openai/gpt-image-1.5:free', name: 'GPT Image 1.5', description: 'OpenAI · Free', credits: 0, category: 'always-free' },
      { id: 'Tongyi-MAI/Z-Image-Turbo:free', name: 'Z Image Turbo', description: 'Fast · Free', credits: 0, category: 'always-free' },
      { id: 'stabilityai/sdxl-turbo:free', name: 'SDXL Turbo', description: 'Quick · Free', credits: 0, category: 'always-free' }
    ];

    const premiumModels = [
      { id: 'black-forest-labs/FLUX-2-pro', name: 'FLUX 2 Pro', description: 'High quality', credits: 4, category: 'premium' },
      { id: 'bytedance/seedream-4.5', name: 'Seedream 4.5', description: 'Photoreal', credits: 5, category: 'premium' },
      { id: 'openai/gpt-image-2', name: 'GPT Image 2', description: 'OpenAI flagship', credits: 6, category: 'premium' },
      { id: 'ideogram-ai/ideogram-v4', name: 'Ideogram V4', description: 'Text in images', credits: 8, category: 'premium' },
      { id: 'google/nano-banana-2', name: 'Nano Banana 2 HQ', description: 'Google, high quality', credits: 9, category: 'premium' },
      { id: 'black-forest-labs/FLUX-2-max', name: 'FLUX 2 Max', description: 'Maximum detail', credits: 9, category: 'premium' },
      { id: 'qwen/qwen-image-2-pro', name: 'Qwen Image 2 Pro', description: 'Qwen pro', credits: 10, category: 'premium' },
      { id: 'google/gemini-3-pro', name: 'Gemini 3 Pro', description: 'Top tier', credits: 18, category: 'premium' }
    ];

    return [...freeAllowanceModels, ...alwaysFreeModels, ...premiumModels];
  }, [credits]);

  // ✅ FIXED: Load user credits - extracted to reusable function
  const loadCredits = useCallback(async () => {
    try {
      const creditsData = await getUserCredits();
      setCredits(creditsData.credits || 0);
    } catch (error) {
      console.error('Failed to load credits:', error);
      setError('Unable to load account information. Please refresh the page.');
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadCredits();
    }
  }, [isOpen, loadCredits]);

  // Helper function to get QXAI user hash
  const getUserHash = () => {
    const hash = localStorage.getItem('qxai_user_hash');
    if (!hash) {
      throw new Error('User not authenticated. Please sign in again.');
    }
    return hash;
  };

  // Helper function to get API URL
  const getApiUrl = () => {
    return process.env.REACT_APP_API_URL || 'https://vtaas-backend-220419304601.us-central1.run.app';
  };

  // ✅ FIXED: Upload image to storage with proper state management
  const uploadImageToStorage = useCallback(async (dataUrl) => {
    const cacheKey = hashString(dataUrl);

    // Check cache first
    if (imageStorageUrls.has(cacheKey)) {
      return imageStorageUrls.get(cacheKey);
    }

    setIsUploading(true);
    try {
      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      // Create form data
      const formData = new FormData();
      formData.append('file', blob, 'image.png');

      // Upload to backend storage
      const userHash = getUserHash();
      const uploadResponse = await fetch(`${getApiUrl()}/storage/upload`, {
        method: 'POST',
        headers: {
          'X-User-Hash': userHash
        },
        body: formData
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const result = await uploadResponse.json();

      // ✅ FIXED: Properly update Map without mutation
      setImageStorageUrls(prev => new Map(prev).set(cacheKey, result.url));

      return result.url;
    } catch (error) {
      console.error('Upload error:', error);
      throw new Error('Unable to upload image. Please check your connection and try again.');
    } finally {
      setIsUploading(false);
    }
  }, [imageStorageUrls]);

  // Helper to prepare image URL
  const prepareImageUrl = useCallback(async (imageUrl) => {
    // If it's already a storage URL, return as-is
    if (imageUrl.includes('firebasestorage.googleapis.com') ||
        imageUrl.includes('storage.googleapis.com')) {
      return imageUrl;
    }

    // If it's a data URL, upload it first
    if (imageUrl.startsWith('data:')) {
      return await uploadImageToStorage(imageUrl);
    }

    // Otherwise return as-is (external URL)
    return imageUrl;
  }, [uploadImageToStorage]);

  // ✅ FIXED: Generate Image handler with proper credit validation
  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt to generate an image');
      return;
    }

    if (prompt.length > 1000) {
      setError('Prompt must be less than 1000 characters');
      return;
    }

    // ✅ Rate limiting
    const now = Date.now();
    if (now - lastGenerateTime < 2000) {
      setError('Please wait a moment before generating again');
      return;
    }
    setLastGenerateTime(now);

    // ✅ CRITICAL: Credit validation before generation
    const selectedModelData = imageModels.find(m => m.id === selectedModel);
    const creditCost = selectedModelData?.credits || 0;

    // Check if it's a free allowance model
    if (selectedModelData?.category === 'free-allowance') {
      if (selectedModelData.allowance.used >= selectedModelData.allowance.total) {
        setError(`${selectedModelData.name} monthly allowance exhausted. Please select another model.`);
        return;
      }
    }

    // Check credit balance for premium models
    if (creditCost > 0) {
      const currentCredits = credits || 0;
      if (currentCredits < creditCost) {
        setError(`Insufficient credits. You have ${currentCredits.toFixed(1)} but need ${creditCost} credits. Please add credits to continue.`);
        return;
      }
    }

    setIsGenerating(true);
    setError('');
    setSuccess('');

    try {
      const result = await generateImage({
        prompt: prompt.trim(),
        model: selectedModel,
        aspectRatio: aspectRatio,
        quality: quality
      });

      if (result && (result.image_url || result.generated_image || result.generatedImage)) {
        const imageUrl = result.image_url || result.generated_image || result.generatedImage;
        setGeneratedImage(imageUrl);

        // ✅ CRITICAL: Refresh credits after successful generation
        await loadCredits();

        setSuccess('Image generated successfully!' + (result.free_generation ? ' (Free generation)' : ''));
      } else {
        throw new Error('No image received from server');
      }
    } catch (error) {
      console.error('Generation error:', error);

      // ✅ User-friendly error messages
      let errorMessage = 'Unable to generate image. Please try again.';
      if (error.message?.includes('Insufficient credits')) {
        errorMessage = 'Insufficient credits. Please add credits to your account.';
      } else if (error.message?.includes('quota') || error.message?.includes('limit')) {
        errorMessage = 'Generation limit reached. Please try a different model or upgrade your plan.';
      } else if (error.message?.includes('timeout')) {
        errorMessage = 'Generation timed out. Please try again.';
      }

      setError(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  }, [prompt, selectedModel, aspectRatio, quality, credits, imageModels, lastGenerateTime, loadCredits]);

  const handleImageUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please upload a valid image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedImage(e.target.result);
      setError('');
      setSuccess('Image uploaded successfully');
    };
    reader.onerror = () => {
      setError('Unable to read file. Please try again.');
    };
    reader.readAsDataURL(file);
  }, []);

  // ✅ FIXED: Background Removal handler with proper credit check
  const handleBackgroundRemoval = useCallback(async () => {
    const imageToProcess = uploadedImage || generatedImage;
    if (!imageToProcess) {
      setError('Please upload or generate an image first');
      return;
    }

    // Check credits for background removal (if it costs credits)
    // Assuming it's free for now, but add check if needed

    setIsProcessing(true);
    setError('');
    setSuccess('Removing background...');

    try {
      const imageUrl = await prepareImageUrl(imageToProcess);

      const result = await removeBackground(imageUrl);

      if (result && (result.processed_image || result.processedImage)) {
        const processedUrl = result.processed_image || result.processedImage;
        setGeneratedImage(processedUrl);

        // ✅ Refresh credits if operation costs credits
        await loadCredits();

        setSuccess('Background removed successfully!');
      } else {
        throw new Error('No processed image received');
      }
    } catch (error) {
      console.error('Background removal error:', error);
      setError('Unable to remove background. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [uploadedImage, generatedImage, prepareImageUrl, loadCredits]);

  // ✅ FIXED: FLUX Edit handler with credit validation
  const handleFluxEdit = useCallback(async () => {
    const imageToProcess = uploadedImage || generatedImage;
    if (!imageToProcess) {
      setError('Please upload or generate an image first');
      return;
    }

    if (!editPrompt.trim()) {
      setError('Please enter edit instructions');
      return;
    }

    if (editPrompt.length > 500) {
      setError('Edit instructions must be less than 500 characters');
      return;
    }

    // ✅ Credit validation for FLUX Edit
    const creditCost = fluxQuality === 'dev' ? 0 : fluxQuality === 'pro' ? 4 : 8;
    if (creditCost > 0) {
      const currentCredits = credits || 0;
      if (currentCredits < creditCost) {
        setError(`Insufficient credits. You have ${currentCredits.toFixed(1)} but need ${creditCost} credits.`);
        return;
      }
    }

    setIsProcessing(true);
    setError('');
    setSuccess('Uploading image...');

    try {
      const imageUrl = await prepareImageUrl(imageToProcess);
      setSuccess('Processing edit...');

      const result = await fluxEdit({
        imageUrl,
        prompt: editPrompt.trim(),
        quality: fluxQuality
      });

      if (result && result.processed_image) {
        setGeneratedImage(result.processed_image);

        // ✅ Refresh credits after edit
        await loadCredits();

        setSuccess('Image edited successfully!');
        setEditPrompt('');
      } else {
        throw new Error('No edited image received');
      }
    } catch (error) {
      console.error('FLUX edit error:', error);
      setError('Unable to edit image. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [uploadedImage, generatedImage, editPrompt, fluxQuality, credits, prepareImageUrl, loadCredits]);

  // ✅ FIXED: Qwen Edit handler with credit validation
  const handleQwenEdit = useCallback(async () => {
    const imageToProcess = uploadedImage || generatedImage;
    if (!imageToProcess) {
      setError('Please upload or generate an image first');
      return;
    }

    if (!qwenEditPrompt.trim()) {
      setError('Please enter edit instructions');
      return;
    }

    if (qwenEditPrompt.length > 500) {
      setError('Edit instructions must be less than 500 characters');
      return;
    }

    // Check Qwen allowance
    const qwenModel = imageModels.find(m => m.id === 'Qwen-Free');
    if (qwenModel && qwenModel.allowance.used >= qwenModel.allowance.total) {
      setError('Qwen monthly allowance exhausted. Please try FLUX Edit or upgrade your plan.');
      return;
    }

    setIsProcessing(true);
    setError('');
    setSuccess('Uploading image...');

    try {
      const imageUrl = await prepareImageUrl(imageToProcess);
      setSuccess('Processing edit...');

      const result = await qwenEdit({
        imageUrl,
        prompt: qwenEditPrompt.trim()
      });

      if (result && result.processed_image) {
        setGeneratedImage(result.processed_image);

        // ✅ Refresh credits after edit
        await loadCredits();

        setSuccess('Image edited successfully with Qwen!');
        setQwenEditPrompt('');
      } else {
        throw new Error('No edited image received');
      }
    } catch (error) {
      console.error('Qwen edit error:', error);
      setError('Unable to edit image with Qwen. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [uploadedImage, generatedImage, qwenEditPrompt, imageModels, prepareImageUrl, loadCredits]);

  const handleDownload = useCallback(() => {
    if (!generatedImage) return;

    try {
      const link = document.createElement('a');
      link.href = generatedImage;
      link.download = `generated-image-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setSuccess('Download started');
    } catch (error) {
      setError('Unable to download image. Please try right-clicking and saving.');
    }
  }, [generatedImage]);

  const handleShare = useCallback((platform) => {
    if (!generatedImage) return;

    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=Check out this AI-generated image!`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`,
      instagram: `https://www.instagram.com/`
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  }, [generatedImage]);

  const handleCopyToClipboard = useCallback(async () => {
    if (!generatedImage) return;

    try {
      // Try to copy image as blob first
      const response = await fetch(generatedImage);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ [blob.type]: blob })
      ]);
      setSuccess('Image copied to clipboard!');
    } catch (error) {
      console.error('Copy error:', error);
      try {
        await navigator.clipboard.writeText(generatedImage);
        setSuccess('Image URL copied to clipboard!');
      } catch (fallbackError) {
        setError('Unable to copy to clipboard');
      }
    }
  }, [generatedImage]);

  // ✅ FIXED: Model selection with credit validation
  const handleModelSelect = useCallback((model) => {
    // Check if model is affordable
    if (model.category === 'premium') {
      const currentCredits = credits || 0;
      if (currentCredits < model.credits) {
        setError(`Insufficient credits for ${model.name}. You have ${currentCredits.toFixed(1)} but need ${model.credits} credits.`);
        return;
      }
    }

    // Check free allowance
    if (model.category === 'free-allowance') {
      if (model.allowance.used >= model.allowance.total) {
        setError(`${model.name} monthly allowance exhausted. Please select another model.`);
        return;
      }
    }

    setSelectedModel(model.id);
    setError(''); // Clear any previous errors
  }, [credits]);

  // Keyboard event handler
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  if (!isOpen) return null;

  const selectedModelData = imageModels.find(model => model.id === selectedModel);
  const creditCost = selectedModelData ? selectedModelData.credits : 0;
  const currentImage = uploadedImage || generatedImage;
  const isLoading = isGenerating || isProcessing || isUploading;
  const currentCredits = credits || 0;

  return (
    <div
      className="vault-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="image-generator-title"
    >
      <div className="vault-modal vault-modal--wide" style={{ maxHeight: '90vh' }}>
        {/* Header */}
        <div className="vault-modal-head">
          <div>
            <h2 id="image-generator-title" className="vault-modal-title">AI Image Generator</h2>
            <div className="vault-modal-sub">Generate · Edit · Compose</div>
          </div>
          <div className="spacer" />
          {credits !== null && (
            <span className="vault-credit" title="Available credits">
              <span className="dot" />
              {currentCredits.toFixed(1)} credits
            </span>
          )}
          <button
            onClick={onClose}
            className="vault-icon-btn"
            aria-label="Close modal"
          >
            <X />
          </button>
        </div>

        {/* Content */}
        <div className="vault-modal-body" style={{ padding: 0, display: 'flex', overflow: 'hidden' }}>
          {/* Left Panel - Controls */}
          <div style={{ width: '50%', padding: '24px', overflowY: 'auto', borderRight: '1px solid var(--rule)' }}>
            {/* Tabs */}
            <div className="vault-tabs" style={{ marginBottom: '24px' }}>
              <button
                onClick={() => setActiveTab('generate')}
                className={`vault-tab ${activeTab === 'generate' ? 'is-active' : ''}`}
                aria-pressed={activeTab === 'generate'}
              >
                Generate
              </button>
              <button
                onClick={() => setActiveTab('edit')}
                className={`vault-tab ${activeTab === 'edit' ? 'is-active' : ''}`}
                aria-pressed={activeTab === 'edit'}
              >
                Edit
              </button>
            </div>

            {/* Generate Tab */}
            {activeTab === 'generate' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Prompt Input */}
                <div>
                  <label htmlFor="prompt" className="vault-label">
                    <Wand2 size={12} /> Prompt
                  </label>
                  <textarea
                    id="prompt"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Describe the image you want to generate..."
                    className="vault-textarea"
                    maxLength={1000}
                    spellCheck={true}
                    aria-describedby="prompt-help"
                  />
                  <div id="prompt-help" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--fg-faint)', marginTop: '6px', fontFamily: 'var(--mono)' }}>
                    <span>Be descriptive for better results</span>
                    <span>{prompt.length}/1000</span>
                  </div>
                </div>

                {/* Model Selection */}
                <div>
                  <label className="vault-label">Model</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                    {/* Always Free */}
                    <div>
                      <h4 className="vault-label" style={{ color: 'var(--accent)', marginBottom: '8px' }}>
                        <Check size={12} /> Always Free (Recommended)
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {imageModels.filter(model => model.category === 'always-free').map((model) => (
                          <button
                            key={model.id}
                            type="button"
                            onClick={() => handleModelSelect(model)}
                            className={`vault-card ${selectedModel === model.id ? 'is-selected' : ''}`}
                          >
                            <div style={{ flex: 1 }}>
                              <div className="title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {model.name}
                                {model.id === 'FLUX-Free' && (
                                  <span className="vault-badge vault-badge--free">Default</span>
                                )}
                              </div>
                              <div className="desc">{model.description}</div>
                            </div>
                            <span className="vault-badge vault-badge--free">Free</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Free Monthly Allowances */}
                    <div>
                      <h4 className="vault-label" style={{ color: 'var(--accent)', marginBottom: '8px' }}>
                        <Gift size={12} /> Free Monthly Allowances
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {imageModels.filter(model => model.category === 'free-allowance').map((model) => {
                          const remaining = model.allowance.total - model.allowance.used;
                          const isExhausted = remaining <= 0;

                          return (
                            <button
                              key={model.id}
                              type="button"
                              onClick={() => handleModelSelect(model)}
                              disabled={isExhausted}
                              className={`vault-card ${selectedModel === model.id ? 'is-selected' : ''}`}
                            >
                              <div style={{ flex: 1 }}>
                                <div className="title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  {model.name}
                                  {isExhausted && (
                                    <span className="vault-badge" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}>Exhausted</span>
                                  )}
                                </div>
                                <div className="desc">{model.description}</div>
                              </div>
                              <span className="vault-credit" style={isExhausted ? { color: 'var(--danger)' } : undefined}>
                                {remaining}/{model.allowance.total} left
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Premium Models */}
                    <div>
                      <h4 className="vault-label" style={{ color: 'oklch(0.80 0.14 70)', marginBottom: '8px' }}>
                        <Gem size={12} /> Premium Models
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {imageModels.filter(model => model.category === 'premium').map((model) => {
                          const canAfford = currentCredits >= model.credits;

                          return (
                            <button
                              key={model.id}
                              type="button"
                              onClick={() => handleModelSelect(model)}
                              disabled={!canAfford}
                              className={`vault-card ${selectedModel === model.id ? 'is-selected' : ''}`}
                            >
                              <div style={{ flex: 1 }}>
                                <div className="title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  {model.name}
                                  {!canAfford && (
                                    <span className="vault-badge">Insufficient Credits</span>
                                  )}
                                </div>
                                <div className="desc">{model.description}</div>
                              </div>
                              <span className="vault-badge vault-badge--premium">{model.credits} credits</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Aspect Ratio */}
                <div>
                  <label className="vault-label">Aspect Ratio</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {ASPECT_RATIOS.map((ratio) => {
                      const RatioIcon = ratio.Icon;
                      return (
                        <button
                          key={ratio.value}
                          type="button"
                          onClick={() => setAspectRatio(ratio.value)}
                          className={`vault-card ${aspectRatio === ratio.value ? 'is-selected' : ''}`}
                        >
                          <RatioIcon size={18} strokeWidth={1.5} />
                          <div>
                            <div className="title">{ratio.label}</div>
                            <div className="desc">{ratio.value}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Generate Button */}
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || !prompt.trim()}
                  className="vault-btn vault-btn--primary"
                  style={{ width: '100%', height: '48px' }}
                >
                  {isGenerating ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Wand2 size={18} />
                      <span>Generate Image</span>
                      {creditCost > 0 && <span>({creditCost} credits)</span>}
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Edit Tab */}
            {activeTab === 'edit' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Image Upload */}
                <div>
                  <label className="vault-label">
                    <Upload size={12} /> Upload Image
                  </label>
                  <div style={{ border: '1px dashed var(--rule)', borderRadius: '4px', padding: '24px', textAlign: 'center', background: 'var(--bg-1)' }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                      style={{ display: 'none' }}
                    />
                    <label
                      htmlFor="image-upload"
                      style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}
                    >
                      <Upload size={28} style={{ color: 'var(--fg-faint)' }} />
                      <span style={{ color: 'var(--fg-dim)', fontSize: '14px' }}>Click to upload an image</span>
                      <span style={{ color: 'var(--fg-faint)', fontSize: '11px', fontFamily: 'var(--mono)' }}>Max 10MB</span>
                    </label>
                  </div>
                </div>

                {/* Edit Tools */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* AI-Powered Tools Section */}
                  <div>
                    <h3 className="vault-label">AI-Powered Tools</h3>

                    {/* Background Removal */}
                    <button
                      onClick={handleBackgroundRemoval}
                      disabled={isLoading || !currentImage}
                      className="vault-card"
                      style={{ marginBottom: '8px' }}
                    >
                      <Scissors size={20} style={{ color: 'var(--accent)' }} />
                      <div style={{ flex: 1 }} className="text-left">
                        <div className="title">Remove Background</div>
                        <div className="desc">AI-powered background removal</div>
                      </div>
                      {isProcessing && <Loader2 size={16} className="animate-spin" style={{ color: 'var(--fg-faint)' }} />}
                    </button>

                    {/* FLUX Edit */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="text"
                          value={editPrompt}
                          onChange={(e) => setEditPrompt(e.target.value)}
                          placeholder="Describe what to change..."
                          className="vault-field"
                          style={{ flex: 1 }}
                          maxLength={500}
                        />
                        <select
                          value={fluxQuality}
                          onChange={(e) => setFluxQuality(e.target.value)}
                          className="vault-select-input"
                          style={{ width: 'auto' }}
                        >
                          <option value="dev">Dev (Free)</option>
                          <option value="pro">Pro (4 credits)</option>
                          <option value="ultra">Ultra (8 credits)</option>
                        </select>
                      </div>
                      <button
                        onClick={handleFluxEdit}
                        disabled={isLoading || !currentImage || !editPrompt.trim()}
                        className="vault-card"
                      >
                        <Palette size={20} style={{ color: 'var(--accent)' }} />
                        <div style={{ flex: 1 }} className="text-left">
                          <div className="title">FLUX Edit</div>
                          <div className="desc">AI-powered image editing</div>
                        </div>
                        {isProcessing && <Loader2 size={16} className="animate-spin" style={{ color: 'var(--fg-faint)' }} />}
                      </button>
                    </div>

                    {/* Qwen Edit */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
                      <input
                        type="text"
                        value={qwenEditPrompt}
                        onChange={(e) => setQwenEditPrompt(e.target.value)}
                        placeholder="Describe changes for Qwen..."
                        className="vault-field"
                        maxLength={500}
                      />
                      <button
                        onClick={handleQwenEdit}
                        disabled={isLoading || !currentImage || !qwenEditPrompt.trim()}
                        className="vault-card"
                      >
                        <Sparkles size={20} style={{ color: 'oklch(0.80 0.14 70)' }} />
                        <div style={{ flex: 1 }} className="text-left">
                          <div className="title">Qwen Edit</div>
                          <div className="desc">Character-consistent editing</div>
                        </div>
                        {isProcessing && <Loader2 size={16} className="animate-spin" style={{ color: 'var(--fg-faint)' }} />}
                      </button>
                    </div>

                    {/* Advanced Edit */}
                    <button
                      onClick={() => setIsAdvancedEditOpen(true)}
                      disabled={!currentImage}
                      className="vault-card"
                    >
                      <Sliders size={20} style={{ color: 'oklch(0.80 0.14 70)' }} />
                      <div style={{ flex: 1 }} className="text-left">
                        <div className="title">Text, Logo & Filters</div>
                        <div className="desc">Add overlays, adjust colors, transform</div>
                      </div>
                      <Brush size={16} style={{ color: 'var(--fg-faint)' }} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Credits Display */}
            {credits !== null && (
              <div style={{ marginTop: '24px', padding: '16px', background: 'var(--bg-1)', borderRadius: '4px', border: '1px solid var(--rule)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span className="vault-label" style={{ margin: 0 }}>Available Credits</span>
                  <span style={{ fontSize: '20px', fontWeight: 600, color: 'var(--accent)', fontFamily: 'var(--mono)' }}>{currentCredits.toFixed(1)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Preview */}
          <div style={{ width: '50%', padding: '24px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, background: 'var(--bg-1)', border: '1px solid var(--rule)', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
              {currentImage ? (
                <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img
                    src={currentImage}
                    alt="Generated or uploaded"
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '2px' }}
                  />

                  {/* Action Buttons Overlay */}
                  <div style={{ position: 'absolute', top: '16px', right: '16px', display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handleDownload}
                      className="vault-icon-btn"
                      style={{ background: 'oklch(0 0 0 / 0.5)', color: 'var(--fg)' }}
                      title="Download"
                    >
                      <Download />
                    </button>
                    <button
                      onClick={handleCopyToClipboard}
                      className="vault-icon-btn"
                      style={{ background: 'oklch(0 0 0 / 0.5)', color: 'var(--fg)' }}
                      title="Copy to clipboard"
                    >
                      <Copy />
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', color: 'var(--fg-faint)' }}>
                  <ImageIcon size={56} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
                  <p style={{ fontSize: '14px' }}>Generate or upload an image to see it here</p>
                </div>
              )}
            </div>

            {/* Share Options */}
            {currentImage && (
              <div style={{ marginTop: '16px' }}>
                <h3 className="vault-label">Share</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                  {[
                    { key: 'twitter', label: 'Twitter' },
                    { key: 'facebook', label: 'Facebook' },
                    { key: 'linkedin', label: 'LinkedIn' },
                    { key: 'instagram', label: 'Instagram' }
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => handleShare(key)}
                      className="vault-btn vault-btn--ghost"
                      style={{ flexDirection: 'column', height: 'auto', padding: '12px 8px', gap: '6px' }}
                    >
                      <ExternalLink size={16} style={{ color: 'var(--accent)' }} />
                      <span style={{ fontSize: '10px' }}>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Status Messages */}
        {(error || success) && (
          <div className="vault-modal-foot" style={{ justifyContent: 'flex-start' }}>
            {error && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--danger)', background: 'var(--danger-tint)', padding: '10px 14px', borderRadius: '2px', width: '100%' }}>
                <AlertCircle size={18} />
                <span style={{ fontSize: '13px' }}>{error}</span>
              </div>
            )}
            {success && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--accent)', background: 'var(--accent-tint)', padding: '10px 14px', borderRadius: '2px', width: '100%' }}>
                <Check size={18} />
                <span style={{ fontSize: '13px' }}>{success}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Advanced Edit Modal */}
      {isAdvancedEditOpen && (
        <AdvancedEditModal
          isOpen={isAdvancedEditOpen}
          onClose={() => setIsAdvancedEditOpen(false)}
          media={{
            type: 'image',
            url: currentImage,
            name: 'generated-image'
          }}
          onApplyChanges={setGeneratedImage}
        />
      )}
    </div>
  );
};

export default ImageGeneratorModal;
